import { FC, useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import {
  CloseOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { Flex, Tooltip, Typography } from "antd";

import { SidebarMenuV2 } from "@components/SidebarMenuV2";
import { UserDropdownV2 } from "@components/UserDropdownV2";
import { Logo } from "@components/Logo";
import usePermissions from "@hooks/usePermissions";

import {
  Layout,
  Header,
  Content,
  SiderContainer,
  MobileOverlay,
  MobileSidebar,
} from "./styles";
import { IPageLayoutProps } from "../types";
import { NotificationBell } from "@components/Notifications/NotificationBell";
import { useCorban } from "@hooks/useCorban";

const { Sider } = Layout;

export const PageLayoutV2: FC<IPageLayoutProps> = ({
  showSider,
  showHeader,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { homeRoute } = usePermissions();
  const { selectedCorban } = useCorban();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);

      if (mobile) {
        setCollapsed(true);
      } else {
        setMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Layout>
      {showHeader && selectedCorban?.fantasy_name && (
        <Header>
          <div style={{ width: 104 }}>
            <Link to={homeRoute}>
              <Logo variant="header" />
            </Link>
          </div>

          <Flex gap={16} align="center">
            <NotificationBell />
            <UserDropdownV2 isMobile={isMobile} />
            <Tooltip title={selectedCorban.fantasy_name}>
              <Typography.Text
                ellipsis
                style={{
                  maxWidth: 220,
                  color: "#fff",
                  fontWeight: 400,
                }}
              >
                {selectedCorban.fantasy_name}
              </Typography.Text>
            </Tooltip>

            {isMobile && (
              <button
                type="button"
                aria-label="toggle-mobile-menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-sidebar"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                style={{
                  color: "#fff",
                  fontSize: 18,
                  cursor: "pointer",
                  background: "transparent",
                  border: 0,
                  padding: 0,
                }}
              >
                {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              </button>
            )}
          </Flex>
        </Header>
      )}

      <Layout>
        {showSider && !isMobile && (
          <SiderContainer>
            <Sider
              width={260}
              collapsedWidth={60}
              collapsible
              theme="light"
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={
                collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
              }
            >
              <SidebarMenuV2 collapsed={collapsed} onCollapse={setCollapsed} />
            </Sider>
          </SiderContainer>
        )}

        {showSider && isMobile && mobileMenuOpen && (
          <>
            <MobileOverlay
              data-testid="mobile-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />

            <MobileSidebar id="mobile-sidebar" data-testid="mobile-sidebar">
              <SidebarMenuV2 onItemClick={() => setMobileMenuOpen(false)} />
            </MobileSidebar>
          </>
        )}

        <Content>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};
